import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Search,
  Clock3,
  Eye,
  CalendarDays,
  Lightbulb,
  GraduationCap,
  Sprout,
  Newspaper,
  PlayCircle,
  Users,
  BookOpen,
  Leaf,
} from "lucide-react";

// =====================================================
// IMAGES
// =====================================================

import BlogHero from "../image/blog-hero.png";
import FeaturedBlog from "../image/featured-blog.png";

import Blog1 from "../image/blog-1.png";
import Blog2 from "../image/blog-2.png";
import Blog3 from "../image/blog-3.png";
import Blog4 from "../image/blog-4.png";
import Blog5 from "../image/blog-5.png";
import Blog6 from "../image/blog-6.png";
import Blog7 from "../image/blog-7.png";
import Blog8 from "../image/blog-8.png";


// =====================================================
// FEATURED ARTICLE
// =====================================================

const featuredArticle = {
  category: "Hydroponics",
  level: "Beginner Guide",
  title: "How Hydroponic Farming Works: A Complete Guide for Beginners",
  description:
    "Learn the basics of hydroponic farming, how it works, its benefits, and why it's the future of sustainable agriculture.",
  date: "Sep 20, 2026",
  readTime: "8 min read",
  views: "2.4K views",
};


// =====================================================
// LATEST ARTICLES
// =====================================================

const articles = [
  {
    image: Blog1,
    category: "Growing Guides",
    title: "Best Crops for Hydroponic Farming in India",
    description:
      "Discover the top crops like lettuce, tomatoes, cucumbers and more suitable for hydroponic farming.",
    date: "Sep 18, 2026",
    readTime: "6 min read",
    slug: "best-crops-for-hydroponic-farming",
  },

  {
    image: Blog2,
    category: "Smart Technology",
    title: "Understanding pH and EC in Hydroponics",
    description:
      "Learn why pH and EC are important and how to maintain the right levels for healthy plant growth.",
    date: "Sep 15, 2026",
    readTime: "7 min read",
    slug: "understanding-ph-and-ec-in-hydroponics",
  },

  {
    image: Blog3,
    category: "Sustainability",
    title: "Water Conservation Through Hydroponic Farming",
    description:
      "See how hydroponics uses up to 90% less water compared to traditional farming methods.",
    date: "Sep 12, 2026",
    readTime: "5 min read",
    slug: "water-conservation-through-hydroponic-farming",
  },

  {
    image: Blog4,
    category: "Crop Management",
    title: "Growing Strawberries in Hydroponics",
    description:
      "Step-by-step guide to cultivating high-quality strawberries using hydroponic systems.",
    date: "Sep 10, 2026",
    readTime: "8 min read",
    slug: "growing-strawberries-in-hydroponics",
  },

  {
    image: Blog5,
    category: "Industry Insights",
    title: "The Future of Vertical Farming in India",
    description:
      "Explore market trends, opportunities and how vertical farming is shaping the future of agriculture.",
    date: "Sep 05, 2026",
    readTime: "6 min read",
    slug: "future-of-vertical-farming-in-india",
  },

  {
    image: Blog6,
    category: "Hydroponics Basics",
    title: "NFT vs DWC vs Aeroponics: Which is Better?",
    description:
      "Compare different hydroponic systems and find the best one for your farming goals.",
    date: "Sep 01, 2026",
    readTime: "7 min read",
    slug: "nft-vs-dwc-vs-aeroponics",
  },

  {
    image: Blog7,
    category: "Smart Technology",
    title: "How IoT is Transforming Modern Farming",
    description:
      "Discover how sensors, automation and data analytics are making farming smarter and more efficient.",
    date: "Aug 28, 2026",
    readTime: "6 min read",
    slug: "how-iot-is-transforming-modern-farming",
  },

  {
    image: Blog8,
    category: "Sustainability",
    title: "Building a Sustainable Food System",
    description:
      "Learn how hydroponics helps produce safe, fresh food while reducing environmental impact.",
    date: "Aug 25, 2026",
    readTime: "5 min read",
    slug: "building-a-sustainable-food-system",
  },
];


// =====================================================
// CATEGORIES
// =====================================================

const categories = [
  {
    name: "All Articles",
    count: 42,
    icon: BookOpen,
  },
  {
    name: "Hydroponics Basics",
    count: 8,
    icon: GraduationCap,
  },
  {
    name: "Growing Guides",
    count: 10,
    icon: Sprout,
  },
  {
    name: "Crop Management",
    count: 6,
    icon: Users,
  },
  {
    name: "Smart Farming Technology",
    count: 6,
    icon: Newspaper,
  },
  {
    name: "Sustainability",
    count: 5,
    icon: Leaf,
  },
  {
    name: "Industry Insights",
    count: 4,
    icon: BookOpen,
  },
  {
    name: "Case Studies",
    count: 6,
    icon: BookOpen,
  },
  {
    name: "Company Updates",
    count: 5,
    icon: Newspaper,
  },
];


// =====================================================
// POPULAR ARTICLES
// =====================================================

const popularArticles = [
  {
    image: Blog1,
    title: "How Hydroponic Farming Works",
    views: "2.4K views",
    time: "8 min read",
    number: "1",
  },
  {
    image: Blog1,
    title: "Best Crops for Hydroponics",
    views: "1.8K views",
    time: "6 min read",
    number: "2",
  },
  {
    image: Blog2,
    title: "Understanding pH and EC",
    views: "1.5K views",
    time: "7 min read",
    number: "3",
  },
  {
    image: Blog3,
    title: "Water Conservation in Farming",
    views: "1.2K views",
    time: "5 min read",
    number: "4",
  },
  {
    image: Blog5,
    title: "Future of Vertical Farming",
    views: "1.1K views",
    time: "6 min read",
    number: "5",
  },
];


// =====================================================
// MAIN BLOG PAGE
// =====================================================

export default function Blog() {
  return (
    <main className="bg-white text-[#10231c]">

      {/* =================================================
          HERO
      ================================================= */}

      <section className="relative overflow-hidden">

        <div className="relative h-[390px] lg:h-[410px]">

          <img
            src={BlogHero}
            alt="Hydroponic farming"
            className="absolute inset-0 h-full w-full object-cover"
          />

          {/* Left white overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-transparent" />

          <div className="relative z-10 mx-auto flex h-full max-w-[1400px] items-center px-5 lg:px-10">

            <div className="max-w-[480px]">

              {/* Breadcrumb */}
              <div className="mb-4 flex items-center gap-2 text-xs text-gray-600">
                <Link
                  to="/"
                  className="hover:text-[#087f4f]"
                >
                  Home
                </Link>

                <span>›</span>

                <span>Blog</span>
              </div>


              {/* Label */}
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-[#16854f]">
                Blog & Knowledge Hub
              </p>


              {/* Heading */}
              <h1 className="text-4xl font-extrabold leading-[1.03] sm:text-5xl">

                Learn. Grow.
                <br />

                Build a{" "}
                <span className="text-[#087f4f]">
                  Greener
                </span>

                <br />

                Tomorrow.

              </h1>


              {/* Description */}
              <p className="mt-4 max-w-[450px] text-sm leading-5 text-gray-600">
                Explore expert insights, practical guides, farming tips
                and industry updates from Polybionic Technologies.
                Knowledge today for a more sustainable tomorrow.
              </p>


              {/* Buttons */}
              <div className="mt-5 flex flex-wrap gap-3">

                <a
                  href="#articles"
                  className="inline-flex items-center gap-2 rounded-full bg-[#087f4f] px-6 py-3 text-xs font-bold text-white transition hover:bg-[#066b42]"
                >
                  Explore Articles
                  <ArrowRight size={15} />
                </a>


                <button className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-6 py-3 text-xs font-bold text-gray-700">

                  <PlayCircle
                    size={18}
                    className="text-[#087f4f]"
                  />

                  Watch Our Videos

                </button>

              </div>

            </div>


            {/* Hero right text */}
            <div className="absolute right-8 top-24 hidden lg:block">

              <p
                className="text-center text-3xl font-semibold italic leading-tight text-black"
                style={{
                  fontFamily: "Georgia, serif",
                }}
              >
                Knowledge
                <br />
                Cultivates
                <br />
                a Better Future
              </p>

              <div className="mx-auto mt-3 h-[3px] w-32 rotate-[-8deg] rounded-full bg-[#65c438]" />

            </div>

          </div>

        </div>

      </section>


      {/* =================================================
          FEATURE BENEFITS
      ================================================= */}

      <section className="border-y border-green-100 bg-[#f3faf6]">

        <div className="mx-auto grid max-w-[1400px] grid-cols-2 lg:grid-cols-6">

          <FeatureItem
            icon={<Lightbulb />}
            title="Expert"
            subtitle="Insights"
          />

          <FeatureItem
            icon={<GraduationCap />}
            title="Practical"
            subtitle="Guides"
          />

          <FeatureItem
            icon={<Sprout />}
            title="Farming"
            subtitle="Tips"
          />

          <FeatureItem
            icon={<Newspaper />}
            title="Industry"
            subtitle="Updates"
          />

          <FeatureItem
            icon={<PlayCircle />}
            title="Video"
            subtitle="Tutorials"
          />

          <FeatureItem
            icon={<Users />}
            title="Community"
            subtitle="Learning"
          />

        </div>

      </section>


      {/* =================================================
          MAIN BLOG AREA
      ================================================= */}

      <section
        id="articles"
        className="py-8 lg:py-10"
      >

        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">

          <div className="grid gap-6 lg:grid-cols-[1fr_235px]">

            {/* ==========================================
                LEFT CONTENT
            ========================================== */}

            <div>

              {/* Featured */}
              <div>

                <h2 className="text-2xl font-extrabold">
                  Featured Article
                </h2>

                <p className="mt-1 text-xs text-gray-600">
                  Our latest and most popular insights on hydroponic
                  farming and smart agriculture.
                </p>

              </div>


              {/* Featured Card */}
              <div className="mt-4 grid overflow-hidden rounded-xl border border-gray-200 bg-white md:grid-cols-2">

                {/* Image */}
                <div className="relative h-[230px] md:h-full">

                  <img
                    src={FeaturedBlog}
                    alt={featuredArticle.title}
                    className="h-full w-full object-cover"
                  />

                  <span className="absolute left-3 top-3 rounded-full bg-[#087f4f] px-3 py-1 text-[9px] font-bold uppercase text-white">
                    Featured
                  </span>

                </div>


                {/* Content */}
                <div className="flex flex-col justify-center p-5 lg:p-6">

                  <div className="flex gap-2">

                    <span className="rounded-md bg-green-50 px-2 py-1 text-[9px] font-semibold text-[#087f4f]">
                      {featuredArticle.category}
                    </span>

                    <span className="rounded-md bg-purple-50 px-2 py-1 text-[9px] font-semibold text-purple-700">
                      {featuredArticle.level}
                    </span>

                  </div>


                  <h3 className="mt-3 text-2xl font-extrabold leading-tight">
                    {featuredArticle.title}
                  </h3>


                  <p className="mt-3 text-sm leading-6 text-gray-600">
                    {featuredArticle.description}
                  </p>


                  <div className="mt-6 flex flex-wrap items-center gap-4 text-[10px] text-gray-500">

                    <span className="flex items-center gap-1">
                      <CalendarDays size={13} />
                      {featuredArticle.date}
                    </span>

                    <span className="flex items-center gap-1">
                      <Clock3 size={13} />
                      {featuredArticle.readTime}
                    </span>

                    <span className="flex items-center gap-1">
                      <Eye size={13} />
                      {featuredArticle.views}
                    </span>

                  </div>


                  <Link
                    to="/blog/hydroponic-farming-guide"
                    className="mt-4 inline-flex w-fit items-center gap-2 text-xs font-bold text-[#087f4f]"
                  >
                    Read More
                    <ArrowRight size={14} />
                  </Link>

                </div>

              </div>


              {/* ========================================
                  LATEST ARTICLES
              ======================================== */}

              <div className="mt-6">

                <h2 className="text-2xl font-extrabold">
                  Latest Articles
                </h2>

                <p className="mt-1 text-xs text-gray-600">
                  Explore our recent articles, guides and updates.
                </p>


                <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">

                  {articles.map((article, index) => (
                    <ArticleCard
                      key={index}
                      article={article}
                    />
                  ))}

                </div>

              </div>

            </div>


            {/* ==========================================
                SIDEBAR
            ========================================== */}

            <aside className="space-y-4">

              {/* Search */}
              <div className="rounded-xl border border-gray-100 bg-[#f8fbf9] p-3">

                <h3 className="text-sm font-bold">
                  Search Articles
                </h3>

                <div className="relative mt-3">

                  <input
                    type="text"
                    placeholder="Search blogs, topics, or keywords..."
                    className="h-10 w-full rounded-lg border border-gray-200 bg-white px-3 pr-10 text-[10px] outline-none focus:border-[#087f4f]"
                  />

                  <Search
                    size={15}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600"
                  />

                </div>

              </div>


              {/* Categories */}
              <div className="rounded-xl border border-gray-100 bg-[#f8fbf9] p-3">

                <h3 className="text-sm font-bold">
                  Categories
                </h3>


                <div className="mt-3 space-y-1">

                  {categories.map((category, index) => {

                    const Icon = category.icon;

                    return (
                      <button
                        key={index}
                        className={`flex w-full items-center justify-between rounded-lg px-2 py-2 text-left text-[10px] ${
                          index === 0
                            ? "bg-[#e0f2e6] font-semibold text-[#087f4f]"
                            : "text-gray-600 hover:bg-green-50"
                        }`}
                      >

                        <span className="flex items-center gap-2">

                          <Icon
                            size={14}
                            className="text-[#087f4f]"
                          />

                          {category.name}

                        </span>

                        <span>
                          {category.count}
                        </span>

                      </button>
                    );

                  })}

                </div>

              </div>


              {/* Newsletter */}
              <div className="relative overflow-hidden rounded-xl bg-[#087f4f] p-5 text-white">

                <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-white/10" />

                <div className="relative">

                  <div className="flex h-11 w-11 items-center justify-center rounded-full border border-white/40">
                    <span className="text-xl">
                      ✉
                    </span>
                  </div>


                  <h3 className="mt-4 text-lg font-extrabold">
                    Stay Updated
                  </h3>

                  <p className="mt-1 text-[10px] leading-4 text-white/80">
                    Get the latest articles, tips and industry updates
                    directly in your inbox.
                  </p>


                  <input
                    type="email"
                    placeholder="Your email address"
                    className="mt-4 h-9 w-full rounded-lg border-0 px-3 text-[10px] text-gray-700 outline-none"
                  />


                  <button className="mt-2 flex h-9 w-full items-center justify-center gap-2 rounded-full border border-white text-[10px] font-bold">
                    Subscribe
                    <ArrowRight size={13} />
                  </button>

                </div>

              </div>


              {/* Popular */}
              <div>

                <h3 className="text-sm font-extrabold">
                  Popular Articles
                </h3>


                <div className="mt-3 space-y-3">

                  {popularArticles.map((article) => (

                    <div
                      key={article.number}
                      className="flex gap-2"
                    >

                      <div className="relative h-12 w-14 flex-shrink-0 overflow-hidden rounded-md">

                        <img
                          src={article.image}
                          alt={article.title}
                          className="h-full w-full object-cover"
                        />

                        <span className="absolute bottom-0 left-0 flex h-4 w-4 items-center justify-center bg-[#087f4f] text-[8px] font-bold text-white">
                          {article.number}
                        </span>

                      </div>


                      <div>

                        <p className="line-clamp-2 text-[10px] font-bold leading-4">
                          {article.title}
                        </p>

                        <div className="mt-1 flex gap-2 text-[8px] text-gray-500">

                          <span>
                            {article.views}
                          </span>

                          <span>
                            {article.time}
                          </span>

                        </div>

                      </div>

                    </div>

                  ))}

                </div>

              </div>

            </aside>

          </div>

        </div>

      </section>


      {/* =================================================
          KNOWLEDGE CTA
      ================================================= */}

      <section className="relative overflow-hidden">

        <div className="absolute inset-0 bg-[#eaf7ef]" />

        <div className="relative mx-auto grid max-w-[1400px] items-center gap-6 px-5 py-6 lg:grid-cols-[1fr_320px] lg:px-10">

          <div>

            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#16854f]">
              Knowledge Drives Change
            </p>

            <h2 className="mt-1 text-2xl font-extrabold leading-tight">
              Let's Learn, Grow and Build
              <br />
              a Sustainable Future Together
            </h2>

            <p className="mt-2 text-xs text-gray-600">
              Explore more articles, guides and success stories.
            </p>


            <Link
              to="/blog"
              className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#087f4f] px-5 py-2 text-[10px] font-bold text-[#087f4f]"
            >
              View All Articles
              <ArrowRight size={13} />
            </Link>

          </div>


          <div className="rounded-xl bg-[#005536] p-5 text-white">

            <div className="space-y-3">

              <CTAItem text="Practical Knowledge" />
              <CTAItem text="Expert Insights" />
              <CTAItem text="Real-World Solutions" />
              <CTAItem text="Sustainable Impact" />

            </div>

          </div>

        </div>

      </section>

    </main>
  );
}


// =====================================================
// FEATURE ITEM
// =====================================================

function FeatureItem({ icon, title, subtitle }) {
  return (
    <div className="flex items-center justify-center gap-2 border-b border-green-100 px-3 py-4 lg:border-b-0 lg:border-r">

      <div className="text-[#087f4f]">
        {React.cloneElement(icon, {
          size: 30,
        })}
      </div>

      <div>

        <p className="text-xs font-semibold">
          {title}
        </p>

        <p className="text-xs font-semibold">
          {subtitle}
        </p>

      </div>

    </div>
  );
}


// =====================================================
// ARTICLE CARD
// =====================================================

function ArticleCard({ article }) {
  return (
    <article className="group overflow-hidden rounded-lg border border-gray-200 bg-white transition hover:-translate-y-1 hover:shadow-md">

      {/* Image */}
      <div className="h-[125px] overflow-hidden">

        <img
          src={article.image}
          alt={article.title}
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
        />

      </div>


      <div className="p-3">

        {/* Category */}
        <span className="inline-flex rounded-md bg-[#e9f7ee] px-2 py-1 text-[8px] font-semibold text-[#087f4f]">
          {article.category}
        </span>


        {/* Title */}
        <h3 className="mt-2 line-clamp-2 text-xs font-bold leading-4">
          {article.title}
        </h3>


        {/* Description */}
        <p className="mt-2 line-clamp-3 text-[9px] leading-4 text-gray-500">
          {article.description}
        </p>


        {/* Meta */}
        <div className="mt-3 flex items-center gap-2 text-[8px] text-gray-500">

          <span className="flex items-center gap-1">
            <CalendarDays size={10} />
            {article.date}
          </span>

          <span className="flex items-center gap-1">
            <Clock3 size={10} />
            {article.readTime}
          </span>

        </div>


        {/* Read More */}
        <Link
          to={`/blog/${article.slug}`}
          className="mt-3 inline-flex items-center gap-1 text-[10px] font-bold text-[#087f4f]"
        >
          Read More
          <ArrowRight size={11} />
        </Link>

      </div>

    </article>
  );
}


// =====================================================
// CTA ITEM
// =====================================================

function CTAItem({ text }) {
  return (
    <div className="flex items-center gap-3 text-xs">

      <div className="flex h-7 w-7 items-center justify-center rounded-full border border-white/30">
        ✓
      </div>

      <span>
        {text}
      </span>

    </div>
  );
}